import { formatDate } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Instrument } from '@prlw/core/monitor/instrument.entity';
import { PinInstrumentService } from '@prlw/core/monitor/pin-instrument.service';
import {
  Observable,
  Subject,
  map,
  of,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import { PriceHistoryGraphService } from './price-history-graph/price-history-graph.service';
import {
  DeliveryTimeGraphService,
  SortMode,
} from './delivery-time-graph/delivery-time-graph.service';
import { ForecastTrend } from '@prlw/core/monitor/forecast-trend.entity';
import { DealStatScale } from '@prlw/core/monitor/monitor.gateway';
import { LastDealsProvider } from '@prlw/core/deals/last-deals.provider';
import { ResponsiveService } from '@prlw/libs/responsive/responsive.service';
import { ScrollAnchorService } from '@prlw/libs/scroll-anchor/scroll-anchor.service';
import { mapResult } from '@prlw/infrastructure/observableWithPending-v2';
import { SyncScrollService } from '@prlw/libs/sync-scroll/sync-scroll.service';
import { BasisDetailsService, TSection } from './basis-details.service';

const FORECAST_DESCRIPTIONS = {
  [ForecastTrend.Up]: 'рост (более 250 \u20BD/т)',
  [ForecastTrend.Down]: 'падение (более -250 \u20BD/т)',
  [ForecastTrend.Lateral]: 'боковой тренд (+/-250 \u20BD/т)',
};

@Component({
  selector: 'prlw-basis-details-ui',
  templateUrl: './basis-details.component.html',
  styleUrls: ['./basis-details.component.scss'],
  providers: [
    PriceHistoryGraphService,
    DeliveryTimeGraphService,
    LastDealsProvider,
    SyncScrollService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class BasisDetailsComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() instrument!: Instrument;
  @Input() unpinned: boolean = true;
  @Input() targetEl: ElementRef | null = null;
  @Input() public isExpanded: boolean | null = null;

  @ViewChild('mobileMenu') elMobileMenu: ElementRef | null = null;

  graphRadioGroupPriceHistoryItems = [
    ['1m', 'День'],
    ['2m', 'Неделя'],
    ['6m', 'Месяц'],
  ].map(([id, label]) => ({ id, label }));

  graphRadioGroupDeliveryTimeItems = [
    ['shipmentTime', 'Срок отгрузки', 'var(--prlw-color-lime)'],
    ['deliveryTime', 'Время в пути', 'var(--prlw-color-delivery-time)'],
  ].map(([id, label, color]) => ({ id, label, color }));

  private destroy$ = new Subject<void>();
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  mobileItems = this.basisDetailsService
    .getItems()
    .filter((item) => !['info'].includes(item.section));

  currentItem$ = this.basisDetailsService.currentItem$;
  items = this.basisDetailsService.getItems();

  manufacturers?: string;
  forecast?: string;

  scale$: Observable<DealStatScale>;
  sortMode$: Observable<SortMode>;

  constructor(
    private pinInstrumentService: PinInstrumentService,
    private priceHistoryGraphService: PriceHistoryGraphService,
    private deliveryTimeGraphService: DeliveryTimeGraphService,
    private responsiveService: ResponsiveService,
    private scrollAnchorService: ScrollAnchorService,
    private basisDetailsService: BasisDetailsService,
  ) {
    this.scale$ = this.priceHistoryGraphService.scale$;
    this.sortMode$ = this.deliveryTimeGraphService.sortMode$;
    this.basisDetailsService.currentItem$
      .pipe(
        withLatestFrom(this.xsmall$),
        tap(([section, xsmall]) => {
          if (xsmall) {
            scrollAnchorService.scrollTo(
              `${section}Anchor`,
              ((this.elMobileMenu?.nativeElement.clientHeight ?? 0) +
                (this.elMobileMenu?.nativeElement.offsetHeight ?? 0)) *
                -1,
            );
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  xsmall$ = this.responsiveService.xsmall$;
  sectionButtonActive$ = this.scrollAnchorService.intersectionAnchor$.pipe(
    map((item) => item.split('Anchor')[0]),
  );

  ngOnChanges(): void {
    this.manufacturers = this.instrument.shipment.plants
      .map((plant) => plant.name)
      .join(', ');
    this.forecast =
      (this.instrument.forecastDate &&
        this.instrument.forecastTrend &&
        `на: ${formatDate(this.instrument.forecastDate, 'dd.MM.yyyy', 'ru')}, ${
          FORECAST_DESCRIPTIONS[this.instrument.forecastTrend]
        }`) ||
      '';
  }

  ngOnInit(): void {
    this.priceHistoryGraphService.setInstrumentCode(
      this.instrument.instrumentCode,
    );
  }

  handleChange(section: string) {
    this.basisDetailsService.setCurrentItem(section as TSection);
  }

  pinEvent(instrument: Instrument) {
    this.pinInstrumentService.pin(instrument);
  }

  public setScale(scale: { id: string }): void {
    this.priceHistoryGraphService.setScale(scale.id as DealStatScale);
  }

  public toggleSortMode(item: { id: string }): void {
    this.deliveryTimeGraphService.setSortMode(item.id as SortMode);
  }

  chartData$ = this.priceHistoryGraphService.chartData$.pipe(
    switchMap((items) =>
      items.pending
        ? of(null)
        : of(items).pipe(
            mapResult,
            map((items) =>
              items.map((item) => ({
                time: item.interval.start.toISOString().split('T')[0],
                value: item.amount,
                open: item.openPrice,
                high: item.highPrice,
                low: item.lowPrice,
                close: item.closePrice,
                avgPrice: item.price,
              })),
            ),
          ),
    ),
  );

  isOtp() {
    return (
      this.instrument?.instrumentCode?.[
        this.instrument.instrumentCode.length - 1
      ] === 'J'
    );
  }

  getIsExpanded() {
    return this.unpinned;
  }
}
